import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Estado do menu lateral — aberto (com rótulos) ou colapsado (só ícones).
 *
 * Vive num contexto e não dentro da `AppSidebar` porque o cabeçalho do topo
 * também precisa dele: no desktop é lá que fica o botão de colapsar, e a
 * moldura da app tem de saber a largura para animar. Fica guardado no
 * `localStorage` — quem trabalha em tabelas largas colapsa uma vez e espera
 * encontrá-lo assim no dia seguinte.
 */

const STORAGE_KEY = "sidebar_collapsed";

interface SidebarState {
  collapsed: boolean;
  toggle: () => void;
  setCollapsed: (value: boolean) => void;
}

const SidebarContext = createContext<SidebarState | null>(null);

const readStored = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

export const SidebarProvider = ({ children }: { children: React.ReactNode }) => {
  const [collapsed, setCollapsedState] = useState(readStored);

  const setCollapsed = useCallback((value: boolean) => {
    setCollapsedState(value);
    try {
      localStorage.setItem(STORAGE_KEY, String(value));
    } catch {
      /* storage indisponível */
    }
  }, []);

  const toggle = useCallback(() => setCollapsed(!collapsed), [collapsed, setCollapsed]);

  // ⌘\ / Ctrl+\ — mesmo atalho do VS Code e do Linear.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "\\" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [toggle]);

  const value = useMemo(
    () => ({ collapsed, toggle, setCollapsed }),
    [collapsed, toggle, setCollapsed]
  );

  return <SidebarContext value={value}>{children}</SidebarContext>;
};

export const useSidebar = (): SidebarState => {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error("useSidebar tem de estar dentro de <SidebarProvider>");
  return ctx;
};
